import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from "react";
import { evictModule, loadModule, NoScreenError } from "./moduleLoader";
import ModuleErrorBoundary from "../components/ModuleErrorBoundary";
import { Btn, EmptyState, Ico, Progress } from "@kit-v5";
import { useT } from "../i18n/i18n";
import { createLoadBar } from "./loadBar";
import { hostEmptyKeys } from "./hostEmpty";
import { reportFrontError, startScreenWatch } from "./screenWatch";
// Порог показа полоски: короче — переход считается мгновенным, индикатор не рисуем вовсе.
const LOAD_BAR_SHOW_DELAY_MS = 200;
// Раз показалась — держим не меньше этого, иначе полоска сама становится вспышкой.
const LOAD_BAR_MIN_VISIBLE_MS = 300;
// Тишина после ПОСЛЕДНЕЙ мутации DOM — нужна только продуктам, которые реально
// дозагружают/перерисовывают себя после mount() (иначе settled сработает на
// промежуточном кадре).
const WAIT_SETTLED_QUIET_MS = 90;
// Потолок на случай продукта, который бесконечно шевелит DOM, — не обычный путь.
const WAIT_SETTLED_MAX_MS = 500;
// Держит шторку, пока свежесмонтированный продукт не "устаканится" (DOM
// перестал меняться), или до жёсткого потолка — владелец утвердил снятие по
// готовности, а не по таймеру. Большинство продуктов монтируются СИНХРОННО
// (mount() уже отработал до вызова этой функции) — если за первый кадр после
// начала наблюдения не случилось ни одной мутации, ждать WAIT_SETTLED_QUIET_MS
// вхолостую незачем, settled фиксируется сразу.
function waitSettled(el, quietMs, maxMs) {
    return new Promise((resolve) => {
        let done = false;
        let sawMutation = false;
        let quiet;
        const finish = () => {
            if (done)
                return;
            done = true;
            obs.disconnect();
            if (quiet)
                clearTimeout(quiet);
            clearTimeout(hard);
            resolve();
        };
        const obs = new MutationObserver(() => {
            sawMutation = true;
            if (quiet)
                clearTimeout(quiet);
            quiet = setTimeout(finish, quietMs);
        });
        obs.observe(el, { childList: true, subtree: true, attributes: true, characterData: true });
        requestAnimationFrame(() => { if (!sawMutation)
            finish(); });
        const hard = setTimeout(finish, maxMs);
    });
}
function ModuleHostInner({ active, ctx, hasProducts }) {
    const t = useT();
    const emptyKeys = hostEmptyKeys(hasProducts);
    const wrapRef = useRef(null);
    const hostRef = useRef(null);
    const currentRef = useRef(null);
    const coverRef = useRef(null);
    const [, setError] = React.useState(null);
    // Экран упал асинхронно (сторож): вместо вечной загрузки — состояние ошибки с повтором.
    const [failed, setFailed] = useState(false);
    // У продукта нет экрана вовсе (entry.js 404, moduleLoader.ts) — отдельное состояние, не крах
    // экрана: тот же текст, что и при прямом заходе на отсутствующий в каталоге id (useProductMenu.ts
    // AccessDenied), но здесь продукт УЖЕ смонтирован бы, если бы у него был front.
    const [noScreen, setNoScreen] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const watchRef = useRef(null);
    const stopWatch = () => { watchRef.current?.dispose(); watchRef.current = null; };
    // Императивная шторка (синхронный DOM, без индикатора внутри) — гасит старый
    // продукт в ТОМ ЖЕ кадре, где он убран, чтобы не мелькнул пустой хост.
    const showCover = () => {
        if (coverRef.current || !wrapRef.current)
            return;
        const c = document.createElement("div");
        c.className = "module-switch-loader";
        wrapRef.current.appendChild(c);
        coverRef.current = c;
    };
    const hideCover = () => {
        const c = coverRef.current;
        if (!c)
            return;
        coverRef.current = null;
        c.classList.add("is-done");
        setTimeout(() => c.remove(), 180);
    };
    // Полоска прогресса — единственный видимый индикатор загрузки, с отложенным
    // показом и минимальным временем жизни (см. LOAD_BAR_* выше), чтобы не рябить
    // на быстрых переходах. Состояние вынесено в loadBar.ts (чистая функция, без React) —
    // это и единственный способ покрыть таймерную логику тестом (jsdom в проекте нет), и
    // гарантия, что isVisible() не рассинхронизируется с React-state между рендерами.
    const [barVisible, setBarVisible] = useState(false);
    const loadBarRef = useRef(null);
    if (!loadBarRef.current) {
        loadBarRef.current = createLoadBar(setBarVisible, LOAD_BAR_SHOW_DELAY_MS, LOAD_BAR_MIN_VISIBLE_MS);
    }
    const startBar = () => loadBarRef.current.start();
    const stopBar = () => loadBarRef.current.stop();
    // ctx на момент РЕАЛЬНОГО mount(), а не на момент запуска эффекта: loadModule асинхронен, и
    // дверь в пределах того же модуля может смениться за время загрузки — эффект updateCtx к тому
    // времени уже отработал вхолостую (пейна ещё не было) и второй раз не придёт, поэтому экран
    // навсегда остался бы с onSections двери, которую пользователь уже покинул.
    const ctxRef = useRef(ctx);
    ctxRef.current = ctx;
    useEffect(() => {
        const host = hostRef.current;
        if (!host)
            return;
        if (!active) {
            const cur = currentRef.current;
            if (cur) {
                stopWatch();
                try {
                    cur.entry.unmount(cur.el);
                }
                catch { /* игнор */ }
                cur.el.remove();
                currentRef.current = null;
            }
            hideCover();
            stopBar();
            return;
        }
        // Смена раздела (active.view) НЕ перемонтирует продукт — экран получает
        // новый раздел живьём через шину platform:section, не через ремоунт.
        if (currentRef.current?.slug === active.module) {
            return;
        }
        let cancelled = false;
        const prev = currentRef.current;
        setFailed(false);
        setNoScreen(false);
        if (prev) {
            showCover();
            stopWatch();
            try {
                prev.entry.unmount(prev.el);
            }
            catch { /* игнор */ }
            prev.el.remove();
            currentRef.current = null;
        }
        startBar();
        (async () => {
            let entry;
            try {
                entry = await loadModule(active.module);
            }
            catch (e) {
                // Гашение полоски — ПЕРЕД проверкой cancelled: старт полоски (startBar выше) уже
                // запланирован, и это единственный путь его отменить, даже если дверь/модуль уже
                // сменились и setError ниже пропускается (T-SHELL-LOADBAR).
                hideCover();
                stopBar();
                if (cancelled)
                    return;
                if (e instanceof NoScreenError) {
                    setNoScreen(true);
                    return;
                }
                setError(() => { throw e instanceof Error ? e : new Error(String(e)); });
                return;
            }
            if (cancelled) {
                hideCover();
                stopBar();
                return;
            }
            const mountCtx = ctxRef.current;
            if (!mountCtx) {
                hideCover();
                stopBar();
                if (cancelled)
                    return;
                setError(() => { throw new Error("OsnovaContext недоступен для монтирования"); });
                return;
            }
            const pane = document.createElement("div");
            pane.className = "module-pane";
            pane.id = "module-root";
            host.appendChild(pane);
            const failScreen = () => {
                const cur = currentRef.current;
                stopWatch();
                if (cur) {
                    try {
                        cur.entry.unmount(cur.el);
                    }
                    catch { /* игнор */ }
                    cur.el.remove();
                    currentRef.current = null;
                }
                hideCover();
                stopBar();
                setFailed(true);
            };
            const watch = startScreenWatch({ slug: active.module, onFail: failScreen });
            watchRef.current = watch;
            try {
                entry.mount(pane, { ...mountCtx, screen: { ready: watch.ready } });
            }
            catch (e) {
                stopWatch();
                reportFrontError({ product: active.module, kind: "render", error: e });
                pane.remove();
                hideCover();
                stopBar();
                if (cancelled)
                    return;
                setError(() => { throw e instanceof Error ? e : new Error(String(e)); });
                return;
            }
            currentRef.current = { el: pane, entry, slug: active.module, view: active.view };
            // Плавное появление (styles.css .module-pane.is-visible, 350мс) — навешивается
            // один раз, двойной rAF даёт браузеру отрисовать opacity:0 до старта transition.
            const reveal = () => requestAnimationFrame(() => requestAnimationFrame(() => pane.classList.add("is-visible")));
            if (!prev) {
                reveal();
                stopBar();
                return;
            }
            await waitSettled(pane, WAIT_SETTLED_QUIET_MS, WAIT_SETTLED_MAX_MS);
            // Гашение — ДО проверки cancelled: см. комментарий у catch(loadModule) выше, тот же инвариант.
            hideCover();
            stopBar();
            if (cancelled)
                return;
            // Шторка и контент проявляются ОДНОВРЕМЕННО: шторка гаснет за 160мс (короче,
            // чем 350мс появления контента), поэтому это один непрерывный кроссфейд, а не
            // два движения подряд — искусственный сдвиг старта контента был лишним полом.
            reveal();
        })();
        return () => { cancelled = true; };
    }, [active?.module, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
    // Дверь сменилась в пределах уже смонтированного модуля (module не изменился, эффект выше не
    // перезапускается) — продукт получает свежий ctx через необязательный updateCtx (см.
    // osnovaContext.ts), не через ремонт React-корня. Триггер — любая смена ССЫЛКИ ctx (тот же
    // контракт, что useProfileDrawer.ts): osnovaCtx в useProductMenu.ts пересобирается useMemo на
    // onSections/installedProducts (смена двери, каталога продуктов — T-SHELL-DOOR-SECTIONS,
    // T-XPRODUCT-DISABLED), а ТАКЖЕ на theme/locale — смену языка/темы смонтированный экран раньше
    // узнавал только после ремонта (F5), потому что deps на эффект были точечные (#185). Ссылка
    // меняется строго при реальной смене входных полей useMemo, лишних вызовов updateCtx нет.
    useEffect(() => {
        const cur = currentRef.current;
        if (!cur || !ctx)
            return;
        // Свежий ctx уходит только СВОЕМУ модулю: ctx.module — модуль текущей двери, cur.slug —
        // модуль смонтированного пейна; при расхождении (пейн уже снят и ждёт отложенного
        // размонтирования React) экран получил бы ctx ЧУЖОЙ двери и штамповал бы ею свои
        // разделы (тикет 64).
        if (cur.slug !== ctx.module)
            return;
        try {
            cur.entry.updateCtx?.(cur.el, { ...ctx, screen: { ready: () => watchRef.current?.ready() } });
        }
        catch { /* необязательный шов — продукт может не поддерживать */ }
    }, [ctx]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        return () => {
            const cur = currentRef.current;
            stopWatch();
            if (cur) {
                try {
                    cur.entry.unmount(cur.el);
                }
                catch { /* игнор */ }
            }
            // dispose(), не stop(): компонент целиком размонтируется — setBarVisible на мёртвом
            // дереве бессмысленен, нужно только снять таймеры, чтобы они не выстрелили впустую.
            loadBarRef.current?.dispose();
        };
    }, []);
    return (_jsxs("div", { className: "module-host-wrap", ref: wrapRef, children: [barVisible && (_jsx("div", { className: "module-load-bar", children: _jsx(Progress, { indeterminate: true, size: "sm" }) })), _jsxs("div", { ref: hostRef, className: "module-host", children: [active && failed && (_jsx("div", { className: "module-host-empty", children: _jsx(EmptyState, { icon: Ico.warn, title: t("shell.screen.failed.title"), description: t("shell.screen.failed.body"), action: _jsx(Btn, { kind: "primary", size: "sm", onClick: () => setAttempt((n) => n + 1), children: t("shell.screen.failed.retry") }) }) })), active && noScreen && (_jsx("div", { className: "module-host-empty", children: _jsx(EmptyState, { icon: Ico.inbox, title: t("shell.product.no_screen.title"), description: t("shell.product.no_screen.body"), action: _jsx(Btn, { kind: "secondary", size: "sm", onClick: () => { evictModule(active.module); setAttempt((n) => n + 1); }, children: t("shell.product.no_screen.retry") }) }) })), !active && (_jsx("div", { className: "module-host-empty", children: _jsx(EmptyState, { icon: Ico.inbox, title: t(emptyKeys.title), description: t(emptyKeys.description) }) }))] })] }));
}
export default function ModuleHost({ active, ctx, hasProducts }) {
    return (_jsx(ModuleErrorBoundary, { slug: active?.module, view: active?.view, children: _jsx(ModuleHostInner, { active: active, ctx: ctx, hasProducts: hasProducts }) }));
}
